import { useMemo, useState, useCallback } from 'react';
import {
  Download,
  Table2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Copy,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/lib/format';
import { evaluateFormula } from '@/lib/formula';

// ─── Types ──────────────────────────────────────────────────────────────────

interface FormulaField {
  id: string;
  name: string;
  formula: string;
  events: string[];
}

interface PivotTableProps {
  data: Record<string, unknown> | undefined;
  className?: string;
  /** Active custom formula field definitions (multiple) */
  formulaFields?: FormulaField[];
  /**
   * Supplementary data per-field (keyed by field ID) with per-event filters applied.
   * When present, formula evaluation uses values from this dataset instead of the main data.
   */
  formulaDataMap?: Record<string, Record<string, unknown>>;
  /** Events explicitly selected by the user (not including formula-only events) */
  selectedEvents?: string[];
  /** Comparison period data (same shape as main data, different date range) */
  comparisonData?: Record<string, unknown>;
}

interface ApiPivotRow {
  row_dimension: string;
  value: number;
  column_breakdown?: string;
  event_name?: string;
}

type TableRow = Record<string, string | number>;
type SortDir = 'asc' | 'desc' | null;
interface SortState {
  key: string;
  dir: SortDir;
}

/**
 * When both column_breakdown and event_name exist, we use grouped headers:
 *   Top row:    | Regular Group (colspan=2) | Text Only (colspan=2) |
 *   Sub row:    | api_call | api_success    | api_call | api_success |
 *
 * Each leaf column's data key is "group\0sub" (null-char separated).
 */
interface ColumnGroup {
  name: string;
  subColumns: string[];
}

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const COL_SEP = '\0'; // separator for composite keys in row data

// ─── Helpers ────────────────────────────────────────────────────────────────

function getHeatmapStyle(
  value: number | undefined | null,
  min: number,
  max: number,
): { bg: string; text: string } {
  if (value == null || min === max) {
    return { bg: 'transparent', text: '#334155' };
  }
  const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const alpha = 0.08 + t * 0.47;
  const bg = `rgba(99, 102, 241, ${alpha})`;
  const text = t > 0.65 ? '#1e1b4b' : '#334155';
  return { bg, text };
}

function getBarWidth(value: number | null | undefined, max: number): number {
  if (value == null || max === 0) return 0;
  return Math.max(2, Math.min(100, (Math.abs(value) / Math.abs(max)) * 100));
}

// ─── Component ──────────────────────────────────────────────────────────────

export function PivotTable({ data, className, formulaFields, formulaDataMap, selectedEvents, comparisonData }: PivotTableProps) {
  const [sort, setSort] = useState<SortState>({ key: '', dir: null });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [heatmapEnabled, setHeatmapEnabled] = useState(false);
  const [barsEnabled, setBarsEnabled] = useState(true);

  // Events that are in ANY formula but NOT in the user's explicit selection
  const formulaOnlyEvents = useMemo(() => {
    if (!formulaFields?.length) return new Set<string>();
    const allFormulaEvents = new Set<string>();
    for (const f of formulaFields) {
      for (const e of f.events) allFormulaEvents.add(e);
    }
    if (!selectedEvents?.length) return allFormulaEvents;
    return new Set([...allFormulaEvents].filter((e) => !selectedEvents.includes(e)));
  }, [formulaFields, selectedEvents]);

  // ── Build per-field lookups from supplementary formula data ──────────────
  // Map: fieldId → Map<groupKey, {eventName: value}>
  const formulaLookups = useMemo(() => {
    if (!formulaDataMap || !formulaFields?.length) return null;

    const result: Record<string, Map<string, Record<string, number>>> = {};

    for (const field of formulaFields) {
      const fieldData = formulaDataMap[field.id];
      if (!fieldData) continue;

      const raw: ApiPivotRow[] =
        (fieldData as { pivot?: ApiPivotRow[] }).pivot ||
        (fieldData as { rows?: ApiPivotRow[] }).rows ||
        [];
      if (raw.length === 0) continue;

      const hasColBreak = raw.some((r) => r.column_breakdown != null && r.column_breakdown !== '');
      const hasEvName = raw.some((r) => r.event_name != null && r.event_name !== '');

      const lookup = new Map<string, Record<string, number>>();

      for (const r of raw) {
        const groupKey = hasColBreak
          ? `${r.row_dimension}${COL_SEP}${r.column_breakdown || '(not set)'}`
          : r.row_dimension;
        let event: string;
        if (hasEvName) {
          event = r.event_name || '(unknown)';
        } else if (!hasColBreak) {
          event = r.row_dimension;
        } else {
          event = r.column_breakdown || 'value';
        }

        if (!lookup.has(groupKey)) lookup.set(groupKey, {});
        lookup.get(groupKey)![event] = r.value;
      }

      result[field.id] = lookup;
    }

    return Object.keys(result).length > 0 ? result : null;
  }, [formulaDataMap, formulaFields]);

  // ── Parse API data ─────────────────────────────────────────────────────
  const { flatColumns, columnGroups, isGrouped, rows, colStats } = useMemo(() => {
    const empty = {
      flatColumns: [] as string[],
      columnGroups: [] as ColumnGroup[],
      isGrouped: false,
      rows: [] as TableRow[],
      colStats: {} as Record<string, { min: number; max: number; total: number }>,
    };
    if (!data) return empty;

    const raw: ApiPivotRow[] =
      (data as { pivot?: ApiPivotRow[] }).pivot ||
      (data as { rows?: ApiPivotRow[] }).rows ||
      [];
    if (raw.length === 0) return empty;

    const hasColumnBreakdown = raw.some(
      (r) => r.column_breakdown != null && r.column_breakdown !== '',
    );
    const hasEventName = raw.some(
      (r) => r.event_name != null && r.event_name !== '',
    );
    const hasBoth = hasColumnBreakdown && hasEventName;

    let rowData: TableRow[];
    let leafKeys: string[];       // data keys on each row
    let groups: ColumnGroup[] = [];

    // Helper: gather event values for a formula evaluation, preferring filtered data
    const gatherEventValues = (
      field: FormulaField,
      allEventCols: string[],
      getMainValue: (eventName: string) => number | undefined,
      groupKey?: string, // for grouped/flat lookups
    ): Record<string, number> => {
      const lookup = formulaLookups?.[field.id];
      const filteredVals = lookup && groupKey ? lookup.get(groupKey) : undefined;
      const eventValues: Record<string, number> = {};
      for (const ev of allEventCols) {
        if (filteredVals && ev in filteredVals) {
          eventValues[ev] = filteredVals[ev];
        } else {
          const v = getMainValue(ev);
          if (v != null) eventValues[ev] = v;
        }
      }
      return eventValues;
    };

    // Helper: evaluate a formula safely
    const safeEval = (formula: string, eventValues: Record<string, number>): number => {
      try {
        return evaluateFormula(formula, eventValues);
      } catch {
        return 0;
      }
    };

    const hasFormulas = formulaFields && formulaFields.length > 0;

    if (!hasColumnBreakdown && !hasEventName) {
      // ── Simple: dimension + value ─────────────────────────────────────
      // Check if dimension values are event names (events-as-rows case)
      const allDims = new Set(raw.map((r) => r.row_dimension));
      const allFormulaEvents = new Set<string>();
      if (hasFormulas) {
        for (const f of formulaFields!) for (const e of f.events) allFormulaEvents.add(e);
      }
      const dimsAreEvents =
        hasFormulas &&
        allDims.size > 0 &&
        [...allDims].some((d) => allFormulaEvents.has(d));

      if (dimsAreEvents && hasFormulas) {
        // ── Events-as-rows: each row is an event, single "value" column ──
        // 1. Filter out formula-only events from display rows
        const displayRows = raw.filter((r) => !formulaOnlyEvents.has(r.row_dimension));
        rowData = displayRows.map((r) => ({ dimension: r.row_dimension, value: r.value }));

        // 2. Compute each formula's value and add as a new row
        const mainValues: Record<string, number> = {};
        for (const r of raw) mainValues[r.row_dimension] = r.value;

        for (const field of formulaFields!) {
          // Check if this field has supplementary filtered data
          const fieldFData = formulaDataMap?.[field.id];
          let eventValues: Record<string, number>;
          if (fieldFData) {
            const formulaRaw: ApiPivotRow[] =
              (fieldFData as { pivot?: ApiPivotRow[] }).pivot ||
              (fieldFData as { rows?: ApiPivotRow[] }).rows ||
              [];
            eventValues = {};
            for (const r of formulaRaw) eventValues[r.row_dimension] = r.value;
          } else {
            eventValues = { ...mainValues };
          }
          rowData.push({ dimension: field.name, value: safeEval(field.formula, eventValues) });
        }

        leafKeys = ['value'];
      } else {
        rowData = raw.map((r) => ({ dimension: r.row_dimension, value: r.value }));
        leafKeys = ['value'];
      }
    } else if (hasBoth) {
      // ── Grouped: column_breakdown × event_name ────────────────────────
      const rowMap = new Map<string, TableRow>();
      const groupMap = new Map<string, Set<string>>(); // group → set of sub-columns

      for (const row of raw) {
        const grp = row.column_breakdown || '(not set)';
        const sub = row.event_name || '(unknown)';
        const leafKey = `${grp}${COL_SEP}${sub}`;

        if (!groupMap.has(grp)) groupMap.set(grp, new Set());
        groupMap.get(grp)!.add(sub);

        if (!rowMap.has(row.row_dimension)) {
          rowMap.set(row.row_dimension, { dimension: row.row_dimension });
        }
        rowMap.get(row.row_dimension)![leafKey] = row.value;
      }

      // Build sorted group structure
      const sortedGroups = Array.from(groupMap.keys()).sort();
      const allSubs = new Set<string>();
      for (const subs of groupMap.values()) {
        for (const s of subs) allSubs.add(s);
      }
      let sortedSubs = Array.from(allSubs).sort();

      // ── Formula fields: compute derived sub-column per group per field ─
      if (hasFormulas) {
        const visibleSubs = sortedSubs.filter((s) => !formulaOnlyEvents.has(s));

        for (const row of rowMap.values()) {
          const dim = row.dimension as string;
          for (const grp of sortedGroups) {
            for (const field of formulaFields!) {
              const lookupKey = `${dim}${COL_SEP}${grp}`;
              const eventValues = gatherEventValues(
                field,
                sortedSubs,
                (ev) => {
                  const key = `${grp}${COL_SEP}${ev}`;
                  const v = row[key];
                  return typeof v === 'number' ? v : undefined;
                },
                lookupKey,
              );
              row[`${grp}${COL_SEP}${field.name}`] = safeEval(field.formula, eventValues);
            }
          }
        }

        sortedSubs = [...visibleSubs, ...formulaFields!.map((f) => f.name)];
      }

      groups = sortedGroups.map((g) => ({ name: g, subColumns: sortedSubs }));
      leafKeys = groups.flatMap((g) =>
        g.subColumns.map((s) => `${g.name}${COL_SEP}${s}`),
      );
      rowData = Array.from(rowMap.values());
    } else {
      // ── Flat: only column_breakdown OR only event_name ────────────────
      const rowMap = new Map<string, TableRow>();
      const colSet = new Set<string>();

      for (const row of raw) {
        const colName = row.column_breakdown || row.event_name || 'value';
        colSet.add(colName);
        if (!rowMap.has(row.row_dimension)) {
          rowMap.set(row.row_dimension, { dimension: row.row_dimension });
        }
        rowMap.get(row.row_dimension)![colName] = row.value;
      }

      let sortedCols = Array.from(colSet).sort();

      // ── Formula fields: compute derived column when columns contain events ─
      if (hasFormulas) {
        const allFormulaEvents = new Set<string>();
        for (const f of formulaFields!) for (const e of f.events) allFormulaEvents.add(e);
        const colsAreEvents = sortedCols.some((c) => allFormulaEvents.has(c));

        if (colsAreEvents) {
          const visibleCols = sortedCols.filter((c) => !formulaOnlyEvents.has(c));

          for (const row of rowMap.values()) {
            const dim = row.dimension as string;
            for (const field of formulaFields!) {
              const eventValues = gatherEventValues(
                field,
                sortedCols,
                (ev) => {
                  const v = row[ev];
                  return typeof v === 'number' ? v : undefined;
                },
                dim,
              );
              row[field.name] = safeEval(field.formula, eventValues);
            }
          }

          sortedCols = [...visibleCols, ...formulaFields!.map((f) => f.name)];
        }
      }

      leafKeys = sortedCols;
      rowData = Array.from(rowMap.values());
    }

    // Compute per-leaf-column stats
    const colStats: Record<string, { min: number; max: number; total: number }> = {};
    for (const key of leafKeys) {
      let min = Infinity;
      let max = -Infinity;
      let total = 0;
      for (const row of rowData) {
        const v = row[key] as number | undefined;
        if (v != null) {
          if (v < min) min = v;
          if (v > max) max = v;
          total += v;
        }
      }
      colStats[key] = {
        min: min === Infinity ? 0 : min,
        max: max === -Infinity ? 0 : max,
        total,
      };
    }

    return {
      flatColumns: leafKeys,
      columnGroups: groups,
      isGrouped: hasBoth,
      rows: rowData,
      colStats,
    };
  }, [data, formulaFields, formulaOnlyEvents, formulaLookups, formulaDataMap]);

  // ── Build comparison lookup: row → { col → value } ──────────────────
  const cmpLookup = useMemo(() => {
    if (!comparisonData) return null;
    const raw: ApiPivotRow[] =
      (comparisonData as { pivot?: ApiPivotRow[] }).pivot ||
      (comparisonData as { rows?: ApiPivotRow[] }).rows ||
      [];
    if (raw.length === 0) return null;

    const map = new Map<string, Record<string, number>>();
    for (const r of raw) {
      if (!map.has(r.row_dimension)) map.set(r.row_dimension, {});
      const bucket = map.get(r.row_dimension)!;
      // Build a key that matches how flatColumns / grouped leaf keys are built
      if (r.column_breakdown && r.event_name) {
        bucket[`${r.column_breakdown}\0${r.event_name}`] = r.value;
      } else if (r.column_breakdown) {
        bucket[r.column_breakdown] = r.value;
      } else if (r.event_name) {
        bucket[r.event_name] = r.value;
      } else {
        bucket['value'] = r.value;
      }
    }
    return map;
  }, [comparisonData]);

  // ── Sorting ────────────────────────────────────────────────────────────
  const sortedRows = useMemo(() => {
    if (!sort.key || !sort.dir) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sort]);

  // ── Pagination ─────────────────────────────────────────────────────────
  const totalPages = Math.ceil(sortedRows.length / pageSize);
  const pagedRows = useMemo(
    () => sortedRows.slice(page * pageSize, (page + 1) * pageSize),
    [sortedRows, page, pageSize],
  );

  const toggleSort = useCallback((key: string) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return { key: '', dir: null };
    });
    setPage(0);
  }, []);

  // ── CSV export ─────────────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    if (rows.length === 0) return;
    const headerLabels = flatColumns.map((c) => {
      if (c.includes(COL_SEP)) {
        const [grp, sub] = c.split(COL_SEP);
        return `${grp} - ${sub}`;
      }
      return c;
    });
    const header = ['dimension', ...headerLabels].join(',');
    const csvRows = rows.map((row) =>
      [row.dimension, ...flatColumns.map((c) => row[c] ?? '')].join(','),
    );
    const csv = [header, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pivot-table.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, flatColumns]);

  const handleCopy = useCallback(() => {
    if (rows.length === 0) return;
    const headerLabels = flatColumns.map((c) => {
      if (c.includes(COL_SEP)) {
        const [grp, sub] = c.split(COL_SEP);
        return `${grp} - ${sub}`;
      }
      return c;
    });
    const header = ['dimension', ...headerLabels].join('\t');
    const tsvRows = rows.map((row) =>
      [row.dimension, ...flatColumns.map((c) => row[c] ?? '')].join('\t'),
    );
    navigator.clipboard.writeText([header, ...tsvRows].join('\n'));
  }, [rows, flatColumns]);

  // ── Empty state ────────────────────────────────────────────────────────
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center">
          <Table2 className="w-6 h-6 text-slate-400" />
        </div>
        <p className="text-sm text-slate-400 font-medium">No data to display</p>
      </div>
    );
  }

  // Unique sub-column count (for grouped badge)
  const subColCount = isGrouped ? columnGroups[0]?.subColumns.length ?? 0 : 0;

  return (
    <div className={cn('w-full', className)}>
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-slate-800">Pivot Table</h3>
          <span className="text-[11px] text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full font-medium tracking-wide">
            {rows.length.toLocaleString()} rows · {flatColumns.length} columns
            {isGrouped && ` · ${columnGroups.length} groups`}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setHeatmapEnabled((p) => !p)}
            className={cn(
              'text-[11px] font-medium px-2.5 py-1.5 rounded-lg border transition-all',
              heatmapEnabled
                ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50',
            )}
          >
            Heatmap
          </button>
          <button
            onClick={() => setBarsEnabled((p) => !p)}
            className={cn(
              'text-[11px] font-medium px-2.5 py-1.5 rounded-lg border transition-all',
              barsEnabled
                ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50',
            )}
          >
            Bars
          </button>
          <div className="w-px h-5 bg-slate-200" />
          <button
            onClick={handleCopy}
            title="Copy to clipboard"
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700
                       bg-white border border-slate-200 rounded-lg px-2.5 py-1.5
                       hover:bg-slate-50 transition-colors"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700
                       bg-white border border-slate-200 rounded-lg px-2.5 py-1.5
                       hover:bg-slate-50 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            CSV
          </button>
        </div>
      </div>

      {/* ── Table container ──────────────────────────────────────────────── */}
      <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white">
        <div className="overflow-auto max-h-[600px] scrollbar-thin">
          <table className="w-full border-collapse min-w-max">
            {/* ── Header ─────────────────────────────────────────────────── */}
            <thead className="sticky top-0 z-10">
              {isGrouped ? (
                <>
                  {/* Row 1: Group headers spanning sub-columns */}
                  <tr className="bg-slate-50/95 backdrop-blur-sm border-b border-slate-200">
                    <th
                      rowSpan={2}
                      className="sticky left-0 z-20 bg-slate-50/95 backdrop-blur-sm
                                 text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500
                                 border-r border-slate-200 cursor-pointer select-none
                                 hover:bg-slate-100/90 transition-colors min-w-[180px]
                                 align-bottom"
                      onClick={() => toggleSort('dimension')}
                    >
                      <span className="flex items-center gap-1.5 pb-1">
                        Dimension
                        <SortIcon current={sort} column="dimension" />
                      </span>
                    </th>
                    {columnGroups.map((grp, gi) => (
                      <th
                        key={grp.name}
                        colSpan={grp.subColumns.length}
                        className={cn(
                          'text-center px-2 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-700',
                          'border-r border-slate-200 last:border-r-0',
                          gi % 2 === 0 ? 'bg-slate-50/95' : 'bg-slate-100/80',
                        )}
                      >
                        {grp.name}
                      </th>
                    ))}
                  </tr>

                  {/* Row 2: Sub-column headers (event names) */}
                  <tr className="bg-slate-50/80 backdrop-blur-sm border-b border-slate-200">
                    {columnGroups.map((grp, gi) =>
                      grp.subColumns.map((sub, si) => {
                        const leafKey = `${grp.name}${COL_SEP}${sub}`;
                        const isLastInGroup = si === grp.subColumns.length - 1;
                        return (
                          <th
                            key={leafKey}
                            className={cn(
                              'text-right px-3 py-2 text-[10px] font-semibold tracking-wider text-slate-500',
                              'cursor-pointer select-none hover:bg-slate-100/90 transition-colors min-w-[100px]',
                              isLastInGroup
                                ? 'border-r border-slate-200 last:border-r-0'
                                : 'border-r border-slate-100',
                              gi % 2 === 0 ? 'bg-slate-50/80' : 'bg-slate-100/60',
                            )}
                            onClick={() => toggleSort(leafKey)}
                          >
                            <span className="flex items-center justify-end gap-1">
                              {sub}
                              <SortIcon current={sort} column={leafKey} />
                            </span>
                          </th>
                        );
                      }),
                    )}
                  </tr>
                </>
              ) : (
                /* ── Flat header (single row) ─────────────────────────────── */
                <tr className="bg-slate-50/95 backdrop-blur-sm border-b border-slate-200">
                  <th
                    className="sticky left-0 z-20 bg-slate-50/95 backdrop-blur-sm
                               text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500
                               border-r border-slate-200 cursor-pointer select-none
                               hover:bg-slate-100/90 transition-colors min-w-[180px]"
                    onClick={() => toggleSort('dimension')}
                  >
                    <span className="flex items-center gap-1.5">
                      Dimension
                      <SortIcon current={sort} column="dimension" />
                    </span>
                  </th>
                  {flatColumns.map((col) => (
                    <th
                      key={col}
                      className="text-right px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500
                                 border-r border-slate-100 last:border-r-0
                                 cursor-pointer select-none hover:bg-slate-100/90 transition-colors min-w-[130px]"
                      onClick={() => toggleSort(col)}
                    >
                      <span className="flex items-center justify-end gap-1.5">
                        {col}
                        <SortIcon current={sort} column={col} />
                      </span>
                    </th>
                  ))}
                </tr>
              )}
            </thead>

            {/* ── Body ───────────────────────────────────────────────────── */}
            <tbody>
              {pagedRows.map((row, rowIdx) => (
                <tr
                  key={String(row.dimension)}
                  className={cn(
                    'border-b border-slate-100 last:border-b-0 transition-colors',
                    'hover:bg-slate-50/80',
                    rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30',
                  )}
                >
                  {/* Dimension cell */}
                  <td
                    className="sticky left-0 z-[5] px-4 py-2.5
                               text-[13px] font-medium text-slate-800
                               border-r border-slate-200
                               bg-inherit"
                  >
                    <span className="truncate block max-w-[260px]" title={String(row.dimension)}>
                      {row.dimension}
                    </span>
                  </td>

                  {/* Data cells */}
                  {isGrouped
                    ? columnGroups.map((grp, gi) =>
                        grp.subColumns.map((sub, si) => {
                          const leafKey = `${grp.name}${COL_SEP}${sub}`;
                          const isLastInGroup = si === grp.subColumns.length - 1;
                          const cmpRow = cmpLookup?.get(String(row.dimension));
                          return (
                            <DataCell
                              key={leafKey}
                              value={row[leafKey] as number | undefined}
                              comparisonValue={cmpRow?.[leafKey]}
                              stats={colStats[leafKey]}
                              heatmapEnabled={heatmapEnabled}
                              barsEnabled={barsEnabled}
                              className={cn(
                                isLastInGroup
                                  ? 'border-r border-slate-200 last:border-r-0'
                                  : 'border-r border-slate-100/60',
                                gi % 2 !== 0 && 'bg-slate-50/20',
                              )}
                            />
                          );
                        }),
                      )
                    : flatColumns.map((col) => {
                        const cmpRow = cmpLookup?.get(String(row.dimension));
                        return (
                          <DataCell
                            key={col}
                            value={row[col] as number | undefined}
                            comparisonValue={cmpRow?.[col]}
                            stats={colStats[col]}
                            heatmapEnabled={heatmapEnabled}
                            barsEnabled={barsEnabled}
                            className="border-r border-slate-100/80 last:border-r-0"
                          />
                        );
                      })}
                </tr>
              ))}
            </tbody>

            {/* ── Totals footer ──────────────────────────────────────────── */}
            <tfoot className="sticky bottom-0 z-10">
              <tr className="bg-slate-50/95 backdrop-blur-sm border-t-2 border-slate-200">
                <td
                  className="sticky left-0 z-20 bg-slate-50/95 backdrop-blur-sm
                             px-4 py-2.5 text-[12px] font-bold text-slate-700 uppercase tracking-wider
                             border-r border-slate-200"
                >
                  Total
                </td>
                {flatColumns.map((col, ci) => {
                  // Determine if this column is last in its group (for thicker border)
                  let isGroupLast = false;
                  if (isGrouped) {
                    let idx = 0;
                    for (const grp of columnGroups) {
                      idx += grp.subColumns.length;
                      if (ci === idx - 1) { isGroupLast = true; break; }
                    }
                  }
                  const total = colStats[col]?.total ?? 0;
                  // Compute comparison total for this column across all rows
                  let cmpTotal: number | undefined;
                  if (cmpLookup) {
                    let sum = 0;
                    cmpLookup.forEach((colMap) => {
                      sum += colMap[col] ?? 0;
                    });
                    cmpTotal = sum;
                  }
                  const totalDelta = cmpTotal != null ? getDelta(total, cmpTotal) : null;
                  return (
                    <td
                      key={col}
                      className={cn(
                        'px-3 text-right',
                        totalDelta ? 'py-1.5' : 'py-2.5',
                        isGrouped && isGroupLast
                          ? 'border-r border-slate-200 last:border-r-0'
                          : 'border-r border-slate-100 last:border-r-0',
                      )}
                    >
                      <span
                        className="text-[13px] font-semibold text-slate-800 tabular-nums"
                        style={{
                          fontFamily: "'JetBrains Mono', ui-monospace, 'Cascadia Code', monospace",
                        }}
                      >
                        {formatNumber(total)}
                      </span>
                      {totalDelta && <DeltaIndicator delta={totalDelta} />}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Pagination ───────────────────────────────────────────────────── */}
      {rows.length > PAGE_SIZE_OPTIONS[0] && (
        <div className="flex items-center justify-between mt-3 px-1">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-400 font-medium">Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              className="text-[12px] text-slate-600 bg-white border border-slate-200 rounded-lg
                         px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500/20
                         focus:border-indigo-300 transition-all"
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-[11px] text-slate-400 font-medium tabular-nums">
              {page * pageSize + 1}–{Math.min((page + 1) * pageSize, rows.length)} of{' '}
              {rows.length.toLocaleString()}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-1 rounded-md border border-slate-200 text-slate-500
                           hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="p-1 rounded-md border border-slate-200 text-slate-500
                           hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Data cell (extracted for reuse) ─────────────────────────────────────────

function DataCell({
  value,
  comparisonValue,
  stats,
  heatmapEnabled,
  barsEnabled,
  className,
}: {
  value: number | undefined;
  comparisonValue?: number;
  stats: { min: number; max: number; total: number } | undefined;
  heatmapEnabled: boolean;
  barsEnabled: boolean;
  className?: string;
}) {
  const s = stats ?? { min: 0, max: 0, total: 0 };
  const hm = heatmapEnabled
    ? getHeatmapStyle(value, s.min, s.max)
    : { bg: 'transparent', text: '#334155' };
  const barW = barsEnabled ? getBarWidth(value, s.max) : 0;

  // Compute comparison delta
  const delta = comparisonValue != null && value != null
    ? getDelta(value, comparisonValue)
    : null;

  return (
    <td
      className={cn(
        'relative px-3 text-right transition-colors duration-200',
        delta ? 'py-1.5' : 'py-2.5',
        className,
      )}
      style={{ backgroundColor: heatmapEnabled ? hm.bg : undefined }}
    >
      {barsEnabled && barW > 0 && (
        <div
          className="absolute top-[6px] bottom-[6px] left-[4px] rounded-[3px] transition-all duration-300"
          style={{
            width: `calc(${barW}% - 8px)`,
            background:
              'linear-gradient(90deg, rgba(99, 102, 241, 0.18), rgba(99, 102, 241, 0.08))',
          }}
        />
      )}
      <span
        className="relative z-[1] text-[13px] tabular-nums"
        style={{
          fontFamily: "'JetBrains Mono', ui-monospace, 'Cascadia Code', monospace",
          color: hm.text,
          fontWeight: value != null && stats && value === stats.max ? 600 : 400,
        }}
      >
        {value != null ? formatNumber(value) : '—'}
      </span>
      {delta && <DeltaIndicator delta={delta} />}
    </td>
  );
}

// ─── Comparison helpers ──────────────────────────────────────────────────────

interface DeltaInfo {
  pct: number;      // percentage change (can be ±Infinity)
  abs: number;      // absolute difference (current - previous)
  direction: 'up' | 'down' | 'flat';
}

function getDelta(current: number, previous: number): DeltaInfo | null {
  if (current === previous) return { pct: 0, abs: 0, direction: 'flat' };
  const abs = current - previous;
  const pct = previous !== 0 ? (abs / Math.abs(previous)) * 100 : (current > 0 ? Infinity : -Infinity);
  return { pct, abs, direction: abs > 0 ? 'up' : 'down' };
}

function DeltaIndicator({ delta }: { delta: DeltaInfo }) {
  if (delta.direction === 'flat') return null;

  const isUp = delta.direction === 'up';
  const color = isUp ? 'text-emerald-600' : 'text-red-500';
  const arrow = isUp ? '▲' : '▼';
  const pctStr = Math.abs(delta.pct) === Infinity
    ? 'new'
    : `${Math.abs(delta.pct).toFixed(1)}%`;

  return (
    <div
      className={cn('relative z-[1] text-[10px] tabular-nums leading-tight mt-0.5', color)}
      style={{ fontFamily: "'JetBrains Mono', ui-monospace, 'Cascadia Code', monospace" }}
    >
      {arrow} {pctStr}
    </div>
  );
}

// ─── Sort indicator icon ─────────────────────────────────────────────────────

function SortIcon({ current, column }: { current: SortState; column: string }) {
  if (current.key !== column || !current.dir) {
    return <ArrowUpDown className="w-3 h-3 text-slate-300" />;
  }
  return current.dir === 'asc' ? (
    <ArrowUp className="w-3 h-3 text-indigo-500" />
  ) : (
    <ArrowDown className="w-3 h-3 text-indigo-500" />
  );
}
